import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Heart, MessageSquareWarning, Send } from "lucide-react";

export default function FeedbackCard() {
  const [sentiment, setSentiment] = useState("love");
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    if (!text.trim()) {
      toast.error("Tell me a little more so I can pass it on.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/feedback", { sentiment, text: text.trim() });
      setSent(true);
      setText("");
      toast.success("Thank you — this genuinely helps shape Zaf.");
    } catch (err) {
      toast.error("Couldn't send that just now. Try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  const prompt = sentiment === "love" ? "I love this because…" : "This could be better because…";

  return (
    <section
      className="mt-6 bg-white rounded-3xl p-8 border border-slate-100 shadow-sm"
      data-testid="feedback-card"
    >
      <h2 className="font-display text-2xl text-slate-800">Tell me what you think</h2>
      <p className="text-slate-500 text-sm mt-0.5">
        Zaf is built around real people like you. Your honest words shape where this goes next.
      </p>

      <div className="mt-5 grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => { setSentiment("love"); setSent(false); }}
          data-testid="feedback-love"
          aria-pressed={sentiment === "love"}
          className={`flex items-center justify-center gap-2 rounded-2xl border px-4 py-3 font-semibold transition-all ${
            sentiment === "love"
              ? "border-[#2E9D96] bg-[#2E9D96]/[0.06] text-[#1F7B75]"
              : "border-slate-200 text-slate-600 hover:border-slate-300"
          }`}
        >
          <Heart size={17} /> I love this
        </button>
        <button
          type="button"
          onClick={() => { setSentiment("improve"); setSent(false); }}
          data-testid="feedback-improve"
          aria-pressed={sentiment === "improve"}
          className={`flex items-center justify-center gap-2 rounded-2xl border px-4 py-3 font-semibold transition-all ${
            sentiment === "improve"
              ? "border-[#3F6E8C] bg-[#3F6E8C]/[0.06] text-[#33586F]"
              : "border-slate-200 text-slate-600 hover:border-slate-300"
          }`}
        >
          <MessageSquareWarning size={17} /> Could be better
        </button>
      </div>

      <textarea
        rows={4}
        maxLength={2000}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={prompt}
        data-testid="feedback-text"
        className="mt-4 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 zaf-focus text-[15px] text-slate-800"
      />

      <button
        type="button"
        onClick={submit}
        disabled={submitting}
        data-testid="feedback-submit"
        className="mt-4 inline-flex items-center gap-2 bg-[#2E9D96] hover:bg-[#1F7B75] disabled:opacity-60 text-white font-semibold px-7 py-3 rounded-full transition-colors"
      >
        <Send size={16} /> {submitting ? "Sending…" : "Send feedback"}
      </button>
      {sent && <span className="ml-3 text-sm text-[#1F7B75] font-semibold">Sent — thank you.</span>}
    </section>
  );
}
